/**
 * voice-v8 — Lightbox-Galerie.
 *
 * Vergrößertes Bild mit Prev/Next-Navigation, Tastatur (←/→/Esc) und
 * Touch-Gesten. Nutzt das vorhandene #lightbox-DOM aus index.html — wir
 * müssen nichts neu rendern, nur Inhalte tauschen.
 *
 * Zoom/Pan (pre.1039, Martins Wunsch „vergrößern UND verschieben"):
 *   - Pinch (2 Finger) zoomt 1×–5× (um die Bildmitte, dann Pan zum Ausrichten).
 *   - Ein-Finger-Ziehen verschiebt das gezoomte Bild (Pan), randbegrenzt.
 *   - Doppeltipp togglet 1× ↔ 2.5×.
 *   - Bei 1× wischt ein horizontaler Swipe zum nächsten/vorherigen Bild.
 *   - Maus: EIN Klick aufs Bild togglet den Zoom (der Zeiger zeigt dazu die Lupe, gezoomt „verkleinern“), Mausrad zoomt,
 *     Ziehen verschiebt das gezoomte Bild. Die Leisten bleiben für die Maus stehen (27.10.2026: seit dem 19.10. blendete
 *     der Klick nur die Leisten aus und zoomte nicht — trotz Lupen-Zeiger).
 *   - Touch: Tippen aufs Bild blendet die Leisten (oben: Teilen, Herunterladen, Schließen; unten: Unterschrift) ein/aus;
 *     Tippen auf die freie Fläche oder Wischen nach unten schließt (19.10.2026).
 *
 * `openGallery(items, startIdx, opts)` mountet eine Session, attached
 * alle Listener, der Close cleant alles wieder weg. Reentrant: zweimal
 * öffnen → erste Session schließt sich automatisch.
 */

import { bildDateiname, bindeDownloadFallback, hatTransparenz } from "./bild-werkzeuge.js";
import { zeichen } from "./zeichen.js";

export interface GalleryItem {
  id: string;
  url: string;
  mime: string;
  caption: string | null;
  source: "user" | "generated" | "edited" | "schmied";
  createdAt: string;
}

interface OpenGalleryOpts {
  onAttach?: (item: GalleryItem) => void;
}

let activeCleanup: (() => void) | null = null;

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_SCALE = 2.5;
const SCHLIESS_WISCHEN = 90;
const RAD_SCHRITT = 1.15;

export function openGallery(
  items: GalleryItem[],
  startIdx: number,
  opts: OpenGalleryOpts = {},
): void {
  if (activeCleanup) activeCleanup();
  if (items.length === 0) return;

  const box = document.getElementById("lightbox");
  const img = document.getElementById(
    "lightbox-img",
  ) as HTMLImageElement | null;
  const meta = document.getElementById("lightbox-meta");
  const counter = document.getElementById("lightbox-counter");
  const prev = document.getElementById("lightbox-prev");
  const next = document.getElementById("lightbox-next");
  const close = document.getElementById("lightbox-close");
  const attachBtn = document.getElementById(
    "lightbox-attach",
  ) as HTMLButtonElement | null;
  const shareBtn = document.getElementById(
    "lightbox-share",
  ) as HTMLButtonElement | null;
  if (!box || !img || !attachBtn) return;
  const buehne = document.getElementById("lightbox-buehne");
  const dlBtn = document.getElementById(
    "lightbox-download",
  ) as HTMLAnchorElement | null;
  const vorherFokus = document.activeElement as HTMLElement | null;

  // Die Knöpfe zeichnet der Zeichen-Vorrat (kein Dingbat, kein Emoji).
  const zeichne = (el: HTMLElement | null, art: string, gr = 22): void => {
    if (el && !el.firstElementChild) el.innerHTML = zeichen(art, gr);
  };
  zeichne(shareBtn, "teilen");
  zeichne(dlBtn, "herunterladen");
  zeichne(close, "schliessen");
  zeichne(prev, "pfeil-links", 24);
  zeichne(next, "pfeil-rechts", 24);
  box.classList.remove("is-leisten-aus");

  let idx = Math.min(Math.max(0, startIdx), items.length - 1);
  const multi = items.length > 1;

  // --- Zoom/Pan-Zustand ---------------------------------------------------
  let scale = 1;
  let tx = 0;
  let ty = 0;

  const applyTransform = (animate: boolean): void => {
    img.style.transition = animate ? "" : "none";
    img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    img.classList.toggle("is-zoomed", scale > 1.02);
  };

  // Pan so begrenzen, dass das Bild nicht aus dem Sichtfeld fliegt.
  const clampPan = (): void => {
    const maxX = Math.max(0, (img.clientWidth * (scale - 1)) / 2);
    const maxY = Math.max(0, (img.clientHeight * (scale - 1)) / 2);
    tx = Math.min(maxX, Math.max(-maxX, tx));
    ty = Math.min(maxY, Math.max(-maxY, ty));
  };

  const resetZoom = (animate: boolean): void => {
    scale = 1;
    tx = 0;
    ty = 0;
    applyTransform(animate);
  };

  const setZoom = (target: number, animate: boolean): void => {
    scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, target));
    if (scale <= 1.02) {
      resetZoom(animate);
      return;
    }
    clampPan();
    applyTransform(animate);
  };

  const render = (): void => {
    const item = items[idx];
    if (!item) return;
    buehne?.removeAttribute("data-alpha");
    img.src = item.url;
    img.alt = item.caption ?? "";
    if (dlBtn) {
      dlBtn.href = item.url;
      dlBtn.download = bildDateiname(item.url);
    }
    resetZoom(false); // Zoom beim Bilderwechsel zurücksetzen
    if (meta) {
      if (item.caption) {
        meta.textContent = item.caption;
        meta.setAttribute("aria-expanded", "false");
        meta.hidden = false;
      } else {
        meta.hidden = true;
      }
    }
    if (counter) {
      if (multi) {
        counter.textContent = `${idx + 1} / ${items.length}`;
        counter.hidden = false;
      } else {
        counter.hidden = true;
      }
    }
    if (prev) prev.hidden = !multi;
    if (next) next.hidden = !multi;
  };

  const goPrev = (): void => {
    idx = (idx - 1 + items.length) % items.length;
    render();
  };
  const goNext = (): void => {
    idx = (idx + 1) % items.length;
    render();
  };

  const handleAttach = (): void => {
    const it = items[idx];
    if (it) opts.onAttach?.(it);
    cleanup();
  };

  const handleShare = async (): Promise<void> => {
    const item = items[idx];
    if (!item) return;
    const { shareMedia } = await import("./share.js");
    await shareMedia({
      url: item.url,
      title: item.caption ?? "Bild",
      text: item.caption ?? "",
    });
  };

  // Tastatur-Fokus bleibt in der Vollansicht (Tab läuft im Kreis).
  const haltFokus = (ev: KeyboardEvent): void => {
    const ziele = Array.from(
      box.querySelectorAll<HTMLElement>(
        "button:not([hidden]), a[href], [tabindex='0']:not([hidden])",
      ),
    ).filter((el) => !el.hidden);
    if (ziele.length === 0) return;
    const erstes = ziele[0];
    const letztes = ziele[ziele.length - 1];
    const aktiv = document.activeElement;
    if (ev.shiftKey && aktiv === erstes) {
      ev.preventDefault();
      letztes.focus();
    } else if (!ev.shiftKey && aktiv === letztes) {
      ev.preventDefault();
      erstes.focus();
    } else if (!box.contains(aktiv)) {
      ev.preventDefault();
      erstes.focus();
    }
  };

  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === "Tab") haltFokus(ev);
    else if (ev.key === "+" || ev.key === "=") setZoom(scale * RAD_SCHRITT, true);
    else if (ev.key === "-") setZoom(scale / RAD_SCHRITT, true);
    else if (ev.key === "0") resetZoom(true);
    else if (ev.key === "Escape") cleanup();
    else if (ev.key === "ArrowLeft" && multi) goPrev();
    else if (ev.key === "ArrowRight" && multi) goNext();
  };

  // --- Gesten-Engine ------------------------------------------------------
  type Mode = "none" | "pan" | "pinch" | "swipe";
  let mode: Mode = "none";
  let startX = 0;
  let startY = 0;
  let lastX = 0;
  let lastY = 0;
  let pinchStartDist = 1;
  let pinchStartScale = 1;
  let lastTapTime = 0;
  let lastTouchEndTime = 0;
  // True, sobald eine Pinch-Geste lief — bis ALLE Finger oben sind. Verhindert,
  // dass der nachgezogene letzte Finger als (Doppel-)Tipp fehlinterpretiert wird.
  let hadPinch = false;
  let tapTimer = 0;
  const toggleLeisten = (): void => {
    box.classList.toggle("is-leisten-aus");
  };

  const touchDist = (t: TouchList): number =>
    Math.hypot(
      (t[0]?.clientX ?? 0) - (t[1]?.clientX ?? 0),
      (t[0]?.clientY ?? 0) - (t[1]?.clientY ?? 0),
    );

  const onTouchStart = (ev: TouchEvent): void => {
    if (ev.touches.length === 2) {
      mode = "pinch";
      hadPinch = true;
      pinchStartDist = touchDist(ev.touches) || 1;
      pinchStartScale = scale;
      img.style.transition = "none";
    } else if (ev.touches.length === 1) {
      const t = ev.touches[0];
      startX = lastX = t.clientX;
      startY = lastY = t.clientY;
      mode = scale > 1.02 ? "pan" : "swipe";
      if (mode === "pan") img.style.transition = "none";
    }
  };

  const onTouchMove = (ev: TouchEvent): void => {
    if (mode === "pinch" && ev.touches.length >= 2) {
      ev.preventDefault();
      const d = touchDist(ev.touches);
      setZoom(pinchStartScale * (d / pinchStartDist), false);
    } else if (mode === "pan" && ev.touches.length === 1) {
      ev.preventDefault();
      const t = ev.touches[0];
      tx += t.clientX - lastX;
      ty += t.clientY - lastY;
      lastX = t.clientX;
      lastY = t.clientY;
      clampPan();
      applyTransform(false);
    }
    // mode === "swipe": Default zulassen (Swipe-Nav läuft über touchend).
  };

  const onTouchEnd = (ev: TouchEvent): void => {
    lastTouchEndTime = Date.now();

    if (mode === "pinch") {
      // Nach Pinch ggf. auf 1× einrasten; ein verbleibender Finger → Pan.
      if (scale <= 1.02) resetZoom(true);
      else applyTransform(true);
      if (ev.touches.length === 1) {
        const t = ev.touches[0];
        lastX = t.clientX;
        lastY = t.clientY;
        // Rest-Finger immer als Pan/None behandeln — NICHT als Swipe, sonst
        // wird sein Loslassen als Tipp gewertet (hadPinch deckt das zusätzlich).
        mode = scale > 1.02 ? "pan" : "none";
        startX = t.clientX;
        startY = t.clientY;
      } else {
        mode = "none";
      }
      if (ev.touches.length === 0) hadPinch = false;
      return;
    }

    if (mode === "swipe") {
      const dx = (ev.changedTouches[0]?.clientX ?? startX) - startX;
      const dy = (ev.changedTouches[0]?.clientY ?? startY) - startY;
      if (!hadPinch && Math.abs(dx) < 10 && Math.abs(dy) < 10) {
        // Tipp — Doppeltipp togglet Zoom.
        const now = Date.now();
        if (now - lastTapTime < 300) {
          if (tapTimer) window.clearTimeout(tapTimer);
          setZoom(scale > 1.02 ? 1 : DOUBLE_TAP_SCALE, true);
          lastTapTime = 0;
        } else {
          lastTapTime = now;
          // Ein einzelner Tipp blendet die Leisten ein/aus — erst nach der Doppeltipp-Frist.
          tapTimer = window.setTimeout(toggleLeisten, 300);
        }
      } else if (
        !hadPinch &&
        scale <= 1.02 &&
        dy > SCHLIESS_WISCHEN &&
        dy > Math.abs(dx) * 1.5
      ) {
        // Wischen nach unten schließt.
        cleanup();
      } else if (
        !hadPinch &&
        multi &&
        Math.abs(dx) > 40 &&
        Math.abs(dx) > Math.abs(dy)
      ) {
        if (dx > 0) goPrev();
        else goNext();
      }
      mode = "none";
      if (ev.touches.length === 0) hadPinch = false;
      return;
    }

    if (mode === "pan") {
      img.style.transition = "";
      mode = "none";
    }
    if (ev.touches.length === 0) hadPinch = false;
  };

  // iOS feuert bei System-Gesten / eingehendem Anruf / Palm-Rejection
  // touchcancel statt touchend → Zustand sauber zurücksetzen, sonst bleibt
  // mode/transition hängen bis zur nächsten Geste.
  const onTouchCancel = (): void => {
    mode = "none";
    hadPinch = false;
    if (scale <= 1.02) resetZoom(true);
    else applyTransform(true);
  };

  // Maus: ein Klick togglet den Zoom (der Zeiger ist die Lupe). Touch-Geräte feuern nach touchend einen synthetischen
  // Klick → innerhalb 400ms ignorieren (Tippen/Doppeltippen regelt die Gesten-Engine). Nach dem Ziehen kein Klick.
  let gezogen = false;
  const onImgClick = (ev: MouseEvent): void => {
    ev.stopPropagation();
    if (Date.now() - lastTouchEndTime < 400 || gezogen) return;
    setZoom(scale > 1.02 ? 1 : DOUBLE_TAP_SCALE, true);
  };
  // Mausrad zoomt.
  const onWheel = (ev: WheelEvent): void => {
    ev.preventDefault();
    setZoom(scale * (ev.deltaY < 0 ? RAD_SCHRITT : 1 / RAD_SCHRITT), false);
  };
  // Maus: Ziehen verschiebt das gezoomte Bild.
  const onMouseDown = (ev: MouseEvent): void => {
    if (ev.button !== 0 || scale <= 1.02) return;
    ev.preventDefault();
    gezogen = false;
    let x = ev.clientX;
    let y = ev.clientY;
    const move = (m: MouseEvent): void => {
      if (Math.abs(m.clientX - x) + Math.abs(m.clientY - y) > 0) gezogen = true;
      tx += m.clientX - x;
      ty += m.clientY - y;
      x = m.clientX;
      y = m.clientY;
      clampPan();
      applyTransform(false);
    };
    const up = (): void => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      window.setTimeout(() => (gezogen = false), 0);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };
  // Tippen auf die freie Fläche neben dem Bild schließt (die Bühne füllt alles, das Bild liegt darauf).
  const onBuehneClick = (ev: MouseEvent): void => {
    if (ev.target === buehne) cleanup();
  };
  // Unterschrift: Tipp klappt auf/zu (Enter und Leertaste ebenso).
  const toggleMeta = (): void => {
    if (!meta) return;
    meta.setAttribute(
      "aria-expanded",
      meta.getAttribute("aria-expanded") === "true" ? "false" : "true",
    );
  };
  const onMetaKey = (ev: KeyboardEvent): void => {
    if (ev.key !== "Enter" && ev.key !== " ") return;
    ev.preventDefault();
    toggleMeta();
  };
  // Durchsichtiges Bild: abgesetzte Fläche.
  const onImgLoad = (): void => {
    if (hatTransparenz(img)) buehne?.setAttribute("data-alpha", "");
  };

  const onClose = (): void => cleanup();
  const closeButtons = box.querySelectorAll<HTMLElement>(
    "[data-lightbox-close]",
  );

  function cleanup(): void {
    box?.setAttribute("hidden", "");
    resetZoom(false);
    if (img) img.style.transition = "";
    document.removeEventListener("keydown", onKey);
    img?.removeEventListener("touchstart", onTouchStart);
    img?.removeEventListener("touchmove", onTouchMove);
    img?.removeEventListener("touchend", onTouchEnd);
    img?.removeEventListener("touchcancel", onTouchCancel);
    img?.removeEventListener("click", onImgClick);
    img?.removeEventListener("load", onImgLoad);
    img?.removeEventListener("mousedown", onMouseDown);
    buehne?.removeEventListener("wheel", onWheel);
    buehne?.removeEventListener("click", onBuehneClick);
    meta?.removeEventListener("click", toggleMeta);
    meta?.removeEventListener("keydown", onMetaKey);
    if (tapTimer) window.clearTimeout(tapTimer);
    vorherFokus?.focus?.();
    prev?.removeEventListener("click", goPrev);
    next?.removeEventListener("click", goNext);
    close?.removeEventListener("click", onClose);
    attachBtn?.removeEventListener("click", handleAttach);
    shareBtn?.removeEventListener("click", onShareClick);
    closeButtons.forEach((el) => el.removeEventListener("click", onClose));
    activeCleanup = null;
  }

  const onShareClick = (): void => void handleShare();

  // Mount
  prev?.addEventListener("click", goPrev);
  next?.addEventListener("click", goNext);
  close?.addEventListener("click", onClose);
  closeButtons.forEach((el) => el.addEventListener("click", onClose));
  attachBtn.addEventListener("click", handleAttach);
  // 27.09.2026 (Ticket 2637): „An Chat anhängen" nur, wenn es ein Ziel gibt.
  // Ohne onAttach schloss der Knopf bloß — im Chat-Bild und bei den Ankern
  // versprach er etwas, das nicht geschah.
  attachBtn.hidden = !opts.onAttach;
  shareBtn?.addEventListener("click", onShareClick);
  if (shareBtn) shareBtn.hidden = false;
  img.addEventListener("click", onImgClick);
  img.addEventListener("load", onImgLoad);
  img.addEventListener("mousedown", onMouseDown);
  buehne?.addEventListener("wheel", onWheel, { passive: false });
  buehne?.addEventListener("click", onBuehneClick);
  meta?.addEventListener("click", toggleMeta);
  meta?.addEventListener("keydown", onMetaKey);
  if (dlBtn) bindeDownloadFallback(dlBtn);
  document.addEventListener("keydown", onKey);
  img.addEventListener("touchstart", onTouchStart, { passive: true });
  img.addEventListener("touchmove", onTouchMove, { passive: false });
  img.addEventListener("touchend", onTouchEnd, { passive: true });
  img.addEventListener("touchcancel", onTouchCancel, { passive: true });

  render();
  box.hidden = false;
  activeCleanup = cleanup;
  // Fokus auf „Schließen“: Escape und Tab arbeiten sofort in der Vollansicht.
  close?.focus?.();
}
